import type { FieldMeta } from "./types";

/**
 * i18n key strings for the translatable unit suffixes rendered next to a field.
 * Brand/symbol units (Koins, km, €, %) are rendered verbatim and intentionally
 * omitted here — the render site falls back to the raw `unit` value.
 */
export const UNIT_LABEL_KEYS: Record<string, string> = {
  days: "adminUI.economy.unit.days",
  steps: "adminUI.economy.unit.steps",
  min: "adminUI.economy.unit.min",
};

/**
 * Editable field definitions grouped by section.
 * Mirrors the marketplace-manager tool's EconomyEditor but adapted for shadcn/ui.
 * Only changed values are sent to the backend.
 *
 * `labelKey` / `sectionKey` / `descriptionKey` hold i18n key strings resolved
 * via t() at the render site (this is a non-component module — no hook access).
 */
export const EDITABLE_FIELDS: FieldMeta[] = [
  // Steps
  {
    key: "dailyStepKoinsCap",
    labelKey: "adminUI.economy.field.dailyStepKoinsCap.label",
    sectionKey: "adminUI.economy.editorSection.steps",
    unit: "Koins",
    descriptionKey: "adminUI.economy.field.dailyStepKoinsCap.desc",
    min: 0,
    max: 200,
    step: 1,
  },
  // Exercise
  {
    key: "dailyExerciseKoinsCap",
    labelKey: "adminUI.economy.field.dailyExerciseKoinsCap.label",
    sectionKey: "adminUI.economy.editorSection.exercise",
    unit: "Koins",
    descriptionKey: "adminUI.economy.field.dailyExerciseKoinsCap.desc",
    min: 0,
    max: 200,
    step: 1,
  },
  {
    key: "exerciseScaleFactor",
    labelKey: "adminUI.economy.field.exerciseScaleFactor.label",
    sectionKey: "adminUI.economy.editorSection.exercise",
    descriptionKey: "adminUI.economy.field.exerciseScaleFactor.desc",
    min: 1,
    max: 100,
    step: 1,
  },
  {
    key: "manualSourcePenalty",
    labelKey: "adminUI.economy.field.manualSourcePenalty.label",
    sectionKey: "adminUI.economy.editorSection.exercise",
    descriptionKey: "adminUI.economy.field.manualSourcePenalty.desc",
    min: 0,
    max: 1,
    step: 0.05,
  },
  // Streaks
  {
    key: "streakRequiredDays",
    labelKey: "adminUI.economy.field.streakRequiredDays.label",
    sectionKey: "adminUI.economy.editorSection.streaks",
    unit: "days",
    descriptionKey: "adminUI.economy.field.streakRequiredDays.desc",
    min: 1,
    max: 30,
    step: 1,
  },
  {
    key: "streakRewardKoins",
    labelKey: "adminUI.economy.field.streakRewardKoins.label",
    sectionKey: "adminUI.economy.editorSection.streaks",
    unit: "Koins",
    descriptionKey: "adminUI.economy.field.streakRewardKoins.desc",
    min: 0,
    max: 500,
    step: 1,
  },
  {
    key: "streakMinStepsForQualifyingDay",
    labelKey: "adminUI.economy.field.streakMinStepsForQualifyingDay.label",
    sectionKey: "adminUI.economy.editorSection.streaks",
    unit: "steps",
    descriptionKey: "adminUI.economy.field.streakMinStepsForQualifyingDay.desc",
    min: 0,
    max: 50000,
    step: 500,
  },
  {
    key: "streakMinActivityMinutesForQualifyingDay",
    labelKey: "adminUI.economy.field.streakMinActivityMinutesForQualifyingDay.label",
    sectionKey: "adminUI.economy.editorSection.streaks",
    unit: "min",
    descriptionKey: "adminUI.economy.field.streakMinActivityMinutesForQualifyingDay.desc",
    min: 0,
    max: 120,
    step: 5,
  },
  // Challenges
  {
    key: "challengeMinBuyIn",
    labelKey: "adminUI.economy.field.challengeMinBuyIn.label",
    sectionKey: "adminUI.economy.editorSection.challenges",
    unit: "Koins",
    descriptionKey: "adminUI.economy.field.challengeMinBuyIn.desc",
    min: 0,
    max: 10000,
    step: 10,
  },
  {
    key: "challengeMaxBuyIn",
    labelKey: "adminUI.economy.field.challengeMaxBuyIn.label",
    sectionKey: "adminUI.economy.editorSection.challenges",
    unit: "Koins",
    descriptionKey: "adminUI.economy.field.challengeMaxBuyIn.desc",
    min: 0,
    max: 50000,
    step: 50,
  },
  {
    key: "challengeMinParticipantsForPaid",
    labelKey: "adminUI.economy.field.challengeMinParticipantsForPaid.label",
    sectionKey: "adminUI.economy.editorSection.challenges",
    descriptionKey: "adminUI.economy.field.challengeMinParticipantsForPaid.desc",
    min: 2,
    max: 100,
    step: 1,
  },
  {
    key: "challengePrizePoolPercentage",
    labelKey: "adminUI.economy.field.challengePrizePoolPercentage.label",
    sectionKey: "adminUI.economy.editorSection.challenges",
    descriptionKey: "adminUI.economy.field.challengePrizePoolPercentage.desc",
    min: 0,
    max: 1,
    step: 0.05,
  },
  {
    key: "challengeHouseBurnPercentage",
    labelKey: "adminUI.economy.field.challengeHouseBurnPercentage.label",
    sectionKey: "adminUI.economy.editorSection.challenges",
    descriptionKey: "adminUI.economy.field.challengeHouseBurnPercentage.desc",
    min: 0,
    max: 1,
    step: 0.05,
  },
  // Marketplace
  {
    key: "referencePricePerTenEuro",
    labelKey: "adminUI.economy.field.referencePricePerTenEuro.label",
    sectionKey: "adminUI.economy.editorSection.marketplace",
    unit: "Koins",
    descriptionKey: "adminUI.economy.field.referencePricePerTenEuro.desc",
    min: 100,
    max: 10000,
    step: 50,
  },
  // Other
  {
    key: "onboardingBonusAmount",
    labelKey: "adminUI.economy.field.onboardingBonusAmount.label",
    sectionKey: "adminUI.economy.editorSection.other",
    unit: "Koins",
    descriptionKey: "adminUI.economy.field.onboardingBonusAmount.desc",
    min: 0,
    max: 5000,
    step: 10,
  },
  {
    key: "activityCooldownMinutes",
    labelKey: "adminUI.economy.field.activityCooldownMinutes.label",
    sectionKey: "adminUI.economy.editorSection.other",
    unit: "min",
    descriptionKey: "adminUI.economy.field.activityCooldownMinutes.desc",
    min: 0,
    max: 120,
    step: 5,
  },
];

/** Group fields by section for rendering. Keyed by the section i18n key. */
export function groupFieldsBySection(fields: FieldMeta[]): Record<string, FieldMeta[]> {
  return fields.reduce<Record<string, FieldMeta[]>>((acc, field) => {
    (acc[field.sectionKey] ??= []).push(field);
    return acc;
  }, {});
}
